import { useMemo } from 'react';
import { Chip, Box, Typography, MenuItem } from '@mui/material';
import AppLayout from '../components/layout/AppLayout';
import CopyIconButton from '../components/ui/CopyIconButton';
import {
  ANNUAL_INCOME_CONFIG,
  LIQUID_NET_WORTH_CONFIG,
  createWealthColumnConfig,
} from '../components/ui/WealthSliderConfig';
import { useLeads, useUpdateLead } from '../hooks/queries/useLeads';
import { type OnboardingUser } from '../services/leads-api';
import {
  ONBOARDING_STATUS,
  ONBOARDING_STATUS_OPTIONS,
  FILING_STATUS,
  FILING_STATUS_OPTIONS,
} from '../types/db-types';
import { STATE_NAMES, US_STATES } from '../constants/states';
import {
  getOnboardingStatusColor,
  getFilingStatusColor,
} from '../utils/statusColors';
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
} from 'material-react-table';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { tableHeaderBackground } from '../theme/palette';

export default function LeadsPage() {
  const { data: leads = [], isLoading: loading, error } = useLeads();
  const updateLeadMutation = useUpdateLead();
  const columns = useMemo<MRT_ColumnDef<OnboardingUser>[]>(
    () => [
      // 1. Created
      {
        accessorFn: row => (row.createdAt ? new Date(row.createdAt) : null),
        id: 'createdAt',
        header: 'Created',
        filterVariant: 'date',
        size: 120,
        enableEditing: false,
        Cell: ({ cell }) => {
          const date = cell.getValue<Date | null>();
          return date
            ? date.toLocaleDateString('en-US', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
              })
            : '-';
        },
      },
      // 2. Status
      {
        accessorKey: 'status',
        header: 'Status',
        filterVariant: 'multi-select',
        filterSelectOptions: ONBOARDING_STATUS_OPTIONS.map(status => ({
          label: status.replace(/_/g, ' ').toLowerCase(),
          value: status,
        })),
        enableGrouping: true,
        editVariant: 'select',
        editSelectOptions: ONBOARDING_STATUS_OPTIONS.map(status => ({
          label: status.replace(/_/g, ' ').toLowerCase(),
          value: status,
        })),
        muiEditTextFieldProps: ({ cell }) => ({
          select: true,
          value: cell.getValue() || '',
          onChange: async event => {
            const newValue = event.target.value;
            const rowId = cell.row.original.id;
            const columnId = cell.column.id;
            if (rowId && columnId && newValue !== cell.getValue()) {
              updateLeadMutation.mutate({
                id: rowId,
                field: columnId,
                value: newValue,
              });
            }
          },
          children: ONBOARDING_STATUS_OPTIONS.map(status => {
            const colors = getOnboardingStatusColor(status);
            return (
              <MenuItem key={status} value={status}>
                <Chip
                  label={status.replace(/_/g, ' ').toLowerCase()}
                  size="small"
                  sx={{
                    backgroundColor: colors.backgroundColor,
                    color: colors.color,
                    fontSize: '0.75rem',
                    fontWeight: 500,
                  }}
                />
              </MenuItem>
            );
          }),
        }),
        size: 150,
        enableEditing: true,
        Cell: ({ cell }) => {
          const status = cell.getValue<string>();
          if (!status) return '-';

          const colors = getOnboardingStatusColor(status as ONBOARDING_STATUS);
          return (
            <Chip
              label={status.replace(/_/g, ' ').toLowerCase()}
              size="small"
              sx={{
                backgroundColor: colors.backgroundColor,
                color: colors.color,
                fontSize: '0.75rem',
                fontWeight: 500,
              }}
            />
          );
        },
      },
      // 3. Email
      {
        accessorKey: 'email',
        header: 'Email',
        filterVariant: 'text',
        size: 250,
        enableEditing: true,
        enableGrouping: false,
        Cell: ({ cell }) => (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              '&:hover .copy-icon': { opacity: 1 },
            }}
          >
            <span>{cell.getValue<string>() || '-'}</span>
            <CopyIconButton
              text={cell.getValue<string>() || ''}
              label="Email"
              className="copy-icon"
            />
          </Box>
        ),
      },
      // 4. Full Name
      {
        accessorKey: 'fullName',
        header: 'Full Name',
        filterVariant: 'text',
        size: 200,
        enableEditing: true,
        enableGrouping: false,
        Cell: ({ cell }) => (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              '&:hover .copy-icon': { opacity: 1 },
            }}
          >
            <span>{cell.getValue<string>() || '-'}</span>
            <CopyIconButton
              text={cell.getValue<string>() || ''}
              label="Name"
              className="copy-icon"
            />
          </Box>
        ),
      },
      // 5. Risk Preference
      {
        accessorKey: 'riskPreference',
        header: 'Risk Preference',
        filterVariant: 'text',
        size: 160,
        enableEditing: false,
      },
      // 6. Annual Income
      createWealthColumnConfig<OnboardingUser>(
        'annualIncome',
        'Annual Income',
        ANNUAL_INCOME_CONFIG,
        150,
        true,
        false
      ),
      // 7. Liquid Net Worth
      createWealthColumnConfig<OnboardingUser>(
        'liquidNetWorth',
        'Liquid Net Worth',
        LIQUID_NET_WORTH_CONFIG,
        180,
        false,
        false
      ),
      // 8. Tax State
      {
        accessorKey: 'taxState',
        header: 'Tax State',
        filterVariant: 'select',
        filterSelectOptions: US_STATES.map(stateCode => ({
          label: `${stateCode} - ${STATE_NAMES[stateCode]}`,
          value: stateCode,
        })),
        editVariant: 'select',
        editSelectOptions: US_STATES.map(stateCode => ({
          label: `${stateCode} - ${STATE_NAMES[stateCode]}`,
          value: stateCode,
        })),
        muiEditTextFieldProps: ({ cell }) => ({
          select: true,
          value: cell.getValue() || '',
          onChange: async event => {
            const newValue = event.target.value;
            const rowId = cell.row.original.id;
            const columnId = cell.column.id;
            if (rowId && columnId && newValue !== cell.getValue()) {
              updateLeadMutation.mutate({
                id: rowId,
                field: columnId,
                value: newValue,
              });
            }
          },
          children: US_STATES.map(stateCode => (
            <MenuItem key={stateCode} value={stateCode}>
              {stateCode} - {STATE_NAMES[stateCode]}
            </MenuItem>
          )),
        }),
        size: 120,
        enableEditing: true,
        enableGrouping: false,
      },
      // 9. Country
      {
        accessorKey: 'country',
        header: 'Country',
        filterVariant: 'text',
        size: 120,
        enableEditing: true,
      },
      // 10. Phone
      {
        accessorKey: 'phone',
        header: 'Phone',
        filterVariant: 'text',
        size: 150,
        enableEditing: true,
        enableGrouping: false,
        Cell: ({ cell }) => (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              '&:hover .copy-icon': { opacity: 1 },
            }}
          >
            <span>{cell.getValue<string>() || '-'}</span>
            <CopyIconButton
              text={cell.getValue<string>() || ''}
              label="Phone"
              className="copy-icon"
            />
          </Box>
        ),
      },
      // 11. Birthday
      {
        accessorFn: row => (row.birthday ? new Date(row.birthday) : null),
        id: 'birthday',
        header: 'Birthday',
        filterVariant: 'date',
        size: 120,
        enableEditing: false,
        enableGrouping: false,
        Cell: ({ cell }) => {
          const date = cell.getValue<Date | null>();
          return date
            ? date.toLocaleDateString('en-US', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
              })
            : '-';
        },
      },
      // 12. Filing Status
      {
        accessorKey: 'filingStatus',
        header: 'Filing Status',
        filterVariant: 'multi-select',
        filterSelectOptions: FILING_STATUS_OPTIONS.map(status => ({
          label: status.replace(/_/g, ' ').toLowerCase(),
          value: status,
        })),
        editVariant: 'select',
        editSelectOptions: FILING_STATUS_OPTIONS.map(status => ({
          label: status.replace(/_/g, ' ').toLowerCase(),
          value: status,
        })),
        enableGrouping: false,
        muiEditTextFieldProps: ({ cell }) => ({
          select: true,
          value: cell.getValue() || '',
          onChange: async event => {
            const newValue = event.target.value;
            const rowId = cell.row.original.id;
            const columnId = cell.column.id;
            if (rowId && columnId && newValue !== cell.getValue()) {
              updateLeadMutation.mutate({
                id: rowId,
                field: columnId,
                value: newValue,
              });
            }
          },
          children: FILING_STATUS_OPTIONS.map(status => {
            const colors = getFilingStatusColor(status);
            return (
              <MenuItem key={status} value={status}>
                <Chip
                  label={status.replace(/_/g, ' ').toLowerCase()}
                  size="small"
                  sx={{
                    backgroundColor: colors.backgroundColor,
                    color: colors.color,
                    fontSize: '0.75rem',
                    fontWeight: 500,
                  }}
                />
              </MenuItem>
            );
          }),
        }),
        size: 150,
        enableEditing: true,
        Cell: ({ cell }) => {
          const status = cell.getValue<string>();
          if (!status) return '-';

          const colors = getFilingStatusColor(status as FILING_STATUS);
          return (
            <Chip
              label={status.replace(/_/g, ' ').toLowerCase()}
              size="small"
              sx={{
                backgroundColor: colors.backgroundColor,
                color: colors.color,
                fontSize: '0.75rem',
                fontWeight: 500,
              }}
            />
          );
        },
      },
      // 13. Interests
      {
        accessorKey: 'interests',
        header: 'Interests',
        filterVariant: 'text',
        size: 200,
        enableEditing: false,
        enableGrouping: false,
      },
      // 14. Selected Investment
      {
        accessorKey: 'selectedInvestment',
        header: 'Selected Investment',
        filterVariant: 'text',
        size: 180,
        enableEditing: false,
        enableGrouping: false,
      },
      // 15. Accreditation Method
      {
        accessorKey: 'accreditationMethod',
        header: 'Accreditation Method',
        filterVariant: 'text',
        size: 200,
        enableEditing: false,
        enableGrouping: false,
      },
      // 16. Updated
      {
        accessorFn: row => (row.updatedAt ? new Date(row.updatedAt) : null),
        id: 'updatedAt',
        header: 'Updated',
        filterVariant: 'date-range',
        size: 120,
        enableEditing: false,
        enableGrouping: false,
        Cell: ({ cell }) => {
          const date = cell.getValue<Date | null>();
          return date
            ? date.toLocaleDateString('en-US', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
              })
            : '-';
        },
      },
    ],
    [updateLeadMutation]
  );

  const table = useMaterialReactTable({
    columns,
    data: leads,
    initialState: {
      showColumnFilters: false,
      columnFilters: [
        {
          id: 'status',
          value: ONBOARDING_STATUS_OPTIONS.filter(
            status => status !== 'not_valid' && status !== 'not_investing'
          ),
        },
      ],
    },
    enableEditing: true,
    editDisplayMode: 'cell',
    enableClickToCopy: false,
    enableTopToolbar: true, // Explicitly enable the top toolbar
    enableGrouping: true,
    // grouping: ['status'],
    renderTopToolbarCustomActions: () => (
      <Typography variant="h4" sx={{ p: 2 }}>
        Leads
      </Typography>
    ),
    muiEditTextFieldProps: ({ cell }) => ({
      variant: 'standard',
      onBlur: async event => {
        const newValue = event.target.value;
        const rowId = cell.row.original.id;
        const columnId = cell.column.id;

        if (rowId && columnId && newValue !== cell.getValue()) {
          updateLeadMutation.mutate({
            id: rowId,
            field: columnId,
            value: newValue,
          });
        }
      },
    }),
    muiTableContainerProps: {
      sx: {
        height: '100%',
        maxHeight: '100%',
        '& .MuiTableCell-head': {
          backgroundColor: tableHeaderBackground,
          color: 'var(--mui-palette-primary-contrastText)',
          fontWeight: 600,
        },
      },
    },
    muiTableBodyRowProps: {
      sx: {
        '&:hover': {
          backgroundColor: 'rgba(25, 118, 210, 0.08)',
        },
        '&:nth-of-type(even)': {
          backgroundColor: 'rgba(0, 0, 0, 0.02)',
        },
      },
    },
  });

  if (loading) {
    return (
      <AppLayout>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            width: '100%',
          }}
        >
          Loading...
        </Box>
      </AppLayout>
    );
  }
  if (error) {
    return (
      <AppLayout>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            width: '100%',
            color: 'error.main',
            textAlign: 'center',
          }}
        >
          {error instanceof Error ? error.message : 'Failed to fetch leads'}
        </Box>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Box
        sx={{
          height: '100%',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <MaterialReactTable table={table} />
          <datalist id="states-datalist">
            {US_STATES.map(stateCode => (
              <option key={stateCode} value={stateCode}>
                {STATE_NAMES[stateCode]}
              </option>
            ))}
          </datalist>
        </LocalizationProvider>
      </Box>
    </AppLayout>
  );
}
